import React, { Fragment, useCallback, useEffect, useState } from "react";
import {
  Box,
  Button,
  Checkbox,
  Input,
  Text,
  IconButton,
  HStack,
  Flex,
  Select,
  Tag,
  TagLabel,
  TagCloseButton,
} from "@chakra-ui/react";
import { BuilderList, FormBuilderBoardProps } from "../../FormBuilderTypes";
import { DeleteIcon } from "../../../icons/DeleteIcon";
import { PlusIcon } from "../../../icons/PlusIcon";
import { nanoid } from "nanoid";

const FormBuilderBoard = ({
  builders,
  toggleRequired,
  deleteBuild,
  tab,
  handleChangeInstruction,
  reOrderTabBuilders,
  toggleReorder,
  addOptions,
  deleteOption,
  editOption,
  tabs,
  kyc,
  editInputList,
}: FormBuilderBoardProps) => {
  const [overItem, setOverItem] = useState<any>();
  const [linkOptions, setLinkOptions] = useState([]);

  const dragStart = (e: React.DragEvent, item: BuilderList) => {
    e.dataTransfer.setData("item", JSON.stringify(item));
  };

  const handleDragEnter = (e: React.DragEvent, item: BuilderList) => {
    e.preventDefault();
    e.stopPropagation();
    setOverItem(item);
  };
 
  const handleDrop = (e: React.DragEvent) => {
    const dragItem = JSON.parse(e.dataTransfer.getData("item"));
    const newBuilder = [...builders];
    const dragIndex = builders.findIndex((i) => i.id === dragItem.id);
    const hoverIndex = builders.findIndex((i) => i.id === overItem.id);  

    if (overItem) {
      if (dragIndex !== -1 && hoverIndex !== -1) {
        newBuilder.splice(dragIndex, 1);
        newBuilder.splice(hoverIndex, 0, dragItem);
      } else if (hoverIndex>= 0) {
        newBuilder.splice(hoverIndex, 0, dragItem);
      } else {
        newBuilder.push(dragItem);
      } 
    };
    reOrderTabBuilders(tab.id, newBuilder);
  };

  const handleOnChangeLinkForm = ({ tabId, itemId, index, event }: any) => {
    const linkedForm = tabs.find((res: any) => res.id === event.target.value);
    editOption(tabId, itemId, index, event, "link", linkedForm);
  };

  const handleLinkOption = useCallback(()=>{
   const avaliableLinkOptions = tabs
    .filter(
      (f: any) =>
        f.id !== tab.id &&
        !f?.linkedToOption &&
        f.builders.every(
          (builder: any) => builder.title !== "Card" && builder.title !== "Bank Info"
        )
    );

    setLinkOptions(avaliableLinkOptions);
  },[tabs, setLinkOptions])

  useEffect(() => {
    handleLinkOption();
  }, [handleLinkOption]);
  

  return (
    <>
      {builders?.length ? (
        builders?.map((item, i) => (
          <Box
            mb={builders.length - 1 === i ? "150" : "3"}
            width={"100%"}
            border={"1px solid"}
            borderColor={"gray.300"}
            borderRadius={"lg"}
            key={item.id}
            draggable
            cursor={"grab"}
            onDrop={handleDrop}
            onDragOver={(e) => handleDragEnter(e, item)}
            onDragStart={(e) => dragStart(e, item)}
          >
            <Box
              display={"flex"}
              alignItems={"center"}
              justifyContent={"space-between"}
              flexWrap={"wrap"}
              borderBottom={"1px solid"}
              borderColor={"gray.300"}
              p="6"
            >
              <Box
                height={"100%"}
                justifyContent={"center"}
                display={"flex"}
                alignItems={"center"}
              >
                <Text
                  mx="3"
                  pe="3"
                  fontSize="md"
                  borderRight={"2px solid"}
                  borderColor={"gray.300"}
                  fontWeight="semibold"
                >
                  {item?.title}
                </Text>
              </Box>
              <HStack>
                {item?.title !== "List" && (
                  <IconButton
                    size="sm"
                    variant="ghost"
                    colorScheme="red"
                    aria-label="Remove cycle"
                    icon={<DeleteIcon w={4} h={4} />}
                    onClick={() => deleteBuild(item?.id, tab.id)}
                  />
                )}
              </HStack>
            </Box>
            <Box pt="5" pb="5" pl="6" pr="6">
              <Input
                value={item.instruction}
                size={"lg"}
                placeholder="Enter instruction here"
                onChange={(e) => handleChangeInstruction(e, item?.id, tab.id)}
                errorBorderColor="red.300"
                isInvalid={!!item?.error?.includes("instruction")}
              />
              {item?.options?.length > 0 && item?.title !== "List" &&
                item?.options.map((opt: any, i: number) => {
                  const invalidOptions =
                      ["radio", "checkbox","dropdown"]?.includes(item?.type) &&
                    item?.options?.length &&
                    opt?.option === "";
                  return(
                  <Fragment key={i}>
                    <Box
                      mt={3}
                      display={"flex"}
                      alignItems={"center"}
                      gap={2}
                    >
                      <Button colorScheme="gray">{`option ${i + 1}`}</Button>
                      <Input
                        size={"md"}
                        placeholder=""
                        value={opt.option}
                        onChange={(e) => editOption(tab.id, item.id, opt.id, e)}
                        errorBorderColor="red.300"
                        isInvalid={invalidOptions && !!item?.error?.includes("options")}
                      />
                      <Flex gap={5}>
                        {!opt.linked && !kyc &&  item?.type!== "checkbox" && (
                          <Button
                            disabled={item.options.length === 1}
                            onClick={(e) =>
                              editOption(tab.id, item?.id, opt.id, e, "link")
                            }
                          >
                            Link to Form
                          </Button>
                        )}
                        <Button
                          disabled={item.options.length === 1}
                          onClick={() => deleteOption(tab.id, item?.id, i)}
                        >
                          <DeleteIcon w={4} />
                        </Button>
                      </Flex>
                    </Box>
                    {opt.linked && !opt.linkedForm && (
                      <Box
                        mt={3}
                        display={"flex"}
                        alignItems={"center"}
                        gap={2}
                        key={nanoid()}
                      >
                        <Select
                          key={nanoid()}
                          placeholder="Select option"
                          onChange={(e) =>
                            handleOnChangeLinkForm({
                              tabId: tab?.id,
                              itemId: item?.id,
                              index: opt.id,
                              event: e,
                            })
                          }
                        >
                          
                       { linkOptions.map((t: any) => (
                          <option value={t.id} key={nanoid()}>
                            {t.name}
                          </option>
                        ))}
                        </Select>

                
                           <TagCloseButton
                            onClick={(e) =>
                              editOption(
                                tab.id,
                                item?.id,
                                opt.id,
                                e,
                                "unlink",
                                opt.linkedForm
                              )
                            }
                          />
                      </Box>
                    )}
                    {opt.linked && opt.linkedForm && (
                      <HStack spacing={4} mb={4} mt={1}>
                        <Text>Linked To:</Text>
                        <Tag
                          size={"lg"}
                          borderRadius="full"
                          variant="solid"
                          colorScheme="blue"
                        >
                          <TagLabel>{opt.linkedForm.name}</TagLabel>
                          <TagCloseButton
                            onClick={(e) =>
                              editOption(
                                tab.id,
                                item?.id,
                                opt.id,
                                e,
                                "unlink",
                                opt.linkedForm
                              )
                            }
                          />
                        </Tag>
                      </HStack>
                    )}
                  </Fragment>
                )})}
              {item?.subtitle?.length > 0 &&
                item?.subtitle.map((sub: string, i: number) => (
                  <Box
                    mt={3}
                    display={"flex"}
                    alignItems={"center"}
                    gap={2}
                    key={i}
                  >
                    <Button colorScheme="gray">SubField</Button>
                    <Input size={"md"} placeholder="" value={sub} />
                  </Box>
                ))}
              {item.title !== "Bank Info" && item.title !== "Card" && (
                <Box
                  display={"flex"}
                  flexDirection={"column"}
                  gap={"2"}
                  mt={"2"}
                >
                  <Checkbox
                    onChange={(e) => toggleRequired(e, item?.id, tab.id)}
                    isChecked={item?.required}
                    colorScheme="blue"
                  >
                    required
                  </Checkbox>
                  {item?.options?.length > 0 && item?.title !== "List" && (
                    <Button
                      onClick={() => addOptions(tab.id, item?.id)}
                      rightIcon={<PlusIcon />}
                    >
                      Add Option
                    </Button>
                  )}
                </Box>
              )}
              {item?.title === "List" &&
                item?.options.map((opt: any, i: number) => (
                  <Fragment key={i}>
                    <Box
                      mt={3}
                      display={"flex"}
                      alignItems={"center"}
                      gap={2}
                    >
                      <Button colorScheme="gray">{`item ${i + 1}`}</Button>
                      <Input
                        size={"md"}
                        placeholder=""
                        value={opt.option}
                        onChange={(e) => editOption(tab.id, item.id, opt.id, e)}
                      />
                      <Flex gap={5}>
                        <Button
                          disabled={item.options.length === 1}
                          onClick={() => deleteOption(tab.id, item?.id, i)}
                        >
                          <DeleteIcon w={4} />
                        </Button>
                      </Flex>
                    </Box>
                    {i === item?.options?.length - 1 && (
                      <Flex justifyContent={"flex-end"} marginTop="10px">
                        <Button
                          onClick={() => addOptions(tab.id, item?.id)}
                          rightIcon={<PlusIcon />}
                        >
                          Add Item
                        </Button>
                      </Flex>
                    )}
                  </Fragment>
                ))}

              {item?.title === "List Inputs" &&
                item?.inputList?.map((opt: any) => (
                  <Flex w="100%" gap={5} key={opt?.id}>
                    <Flex
                      mt={3}
                      display={"flex"}
                      alignItems={"center"}
                      w="100%"
                    >
                      <Button colorScheme="gray">Field 1</Button>
                      <Input
                        size={"md"}
                        placeholder=""
                        value={item?.field1}
                        w="100%"
                        onChange={(e) =>
                          editInputList(tab.id, item.id, e, "field1")
                        }
                        errorBorderColor="red.300"
                        isInvalid={!!item?.error?.includes("field1")}
                      />
                    </Flex>
                    <Flex
                      mt={3}
                      display={"flex"}
                      alignItems={"center"}
                      w="100%"
                    >
                      <Button colorScheme="gray">Field 2</Button>
                      <Input
                        size={"md"}
                        value={item?.field2}
                        w="100%"
                        placeholder=""
                        onChange={(e) =>
                          editInputList(tab.id, item.id, e, "field2")
                        }
                        errorBorderColor="red.300"
                        isInvalid={!!item?.error?.includes("field2")}
                      />
                    </Flex>
                  </Flex>
                ))}
            </Box>
          </Box>
        ))
      ) : (
        <Box
          width="100%"
          display="flex"
          alignItems="center"
          justifyContent="center"
          height="30vh"
          p="2"
          border="1px"
          borderColor="gray.300"
          borderRadius="lg"
        >
          <Text fontWeight={"bold"}>Drag and drop components here</Text>
        </Box>
      )}
    </>
  );
};

export default FormBuilderBoard;
